import React, { useState } from "react";
import { useAuth } from "@/lib/auth";
import AppShell from "@/components/AppShell";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { ShieldCheck, Lock } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/business/PageHeader";

export default function Profile() {
  const { user, updateMe } = useAuth();
  const [form, setForm] = useState({
    full_name: user?.full_name || "",
    phone: user?.phone || "",
    pan: user?.pan || "",
    aadhaar: user?.aadhaar || "",
    dob: user?.dob || "",
  });
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await updateMe(form);
      toast.success("Profile updated");
    } catch {
      toast.error("Couldn't update profile");
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppShell>
      <div className="max-w-4xl mx-auto">
        <PageHeader>
          <PageHeader.Title>Profile &amp; Settings</PageHeader.Title>
        </PageHeader>
        <p className="mt-2 text-sm text-muted-foreground">Manage your identity, tax info and account settings.</p>

        <Card className="mt-6 p-6 border-border flex items-center gap-4">
          <Avatar className="h-16 w-16">
            <AvatarImage src={user?.picture || undefined} />
            <AvatarFallback className="text-lg bg-primary/10 text-primary font-semibold">
              {user?.full_name?.split(" ").map(p => p[0]).slice(0, 2).join("")}
            </AvatarFallback>
          </Avatar>
          <div>
            <div className="font-display text-xl font-semibold">{user?.full_name}</div>
            <div className="text-sm text-muted-foreground">{user?.email} · <span className="capitalize">{user?.role}</span></div>
            <div className="text-xs mt-1">Profile {user?.profile_completion || 0}% complete · Plan: <span className="capitalize">{user?.plan}</span></div>
          </div>
        </Card>

        <Tabs defaultValue="identity" className="mt-6">
          <TabsList>
            <TabsTrigger value="identity" data-testid="tab-identity">Identity</TabsTrigger>
            <TabsTrigger value="security" data-testid="tab-security">Security</TabsTrigger>
          </TabsList>

          <TabsContent value="identity">
            <Card className="p-6 border-border">
              <form onSubmit={save} className="grid sm:grid-cols-2 gap-4" data-testid="profile-form">
                <div>
                  <Label>Full name (PAN match)</Label>
                  <Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className="h-11 mt-1.5" />
                </div>
                <div>
                  <Label>Mobile</Label>
                  <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="h-11 mt-1.5" placeholder="+91 …" />
                </div>
                <div>
                  <Label>PAN</Label>
                  <Input value={form.pan} onChange={(e) => setForm({ ...form, pan: e.target.value.toUpperCase() })} maxLength={10} className="h-11 mt-1.5 font-mono uppercase" />
                </div>
                <div>
                  <Label>Aadhaar</Label>
                  <Input value={form.aadhaar} onChange={(e) => setForm({ ...form, aadhaar: e.target.value })} className="h-11 mt-1.5" />
                </div>
                <div>
                  <Label>Date of birth</Label>
                  <Input type="date" value={form.dob} onChange={(e) => setForm({ ...form, dob: e.target.value })} className="h-11 mt-1.5" />
                </div>
                <div className="sm:col-span-2">
                  <Button type="submit" disabled={saving} data-testid="profile-save">{saving ? "Saving…" : "Save changes"}</Button>
                </div>
              </form>
            </Card>
          </TabsContent>

          <TabsContent value="security">
            <Card className="p-6 border-border space-y-4">
              <div className="flex items-start gap-4">
                <ShieldCheck className="h-5 w-5 text-accent mt-0.5" />
                <div>
                  <div className="font-semibold">Two-factor authentication</div>
                  <div className="text-sm text-muted-foreground">Add an extra layer of security to your account. Coming soon — secure your account with TOTP / SMS.</div>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Lock className="h-5 w-5 text-accent mt-0.5" />
                <div>
                  <div className="font-semibold">Active sessions</div>
                  <div className="text-sm text-muted-foreground">All your active devices appear here. Sign out of others remotely (coming soon).</div>
                </div>
              </div>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </AppShell>
  );
}
